import { useEffect, useState } from 'react';
import { Boxes, ChevronDown, ChevronRight, AlertCircle, Info } from 'lucide-react';
import Card, { CardHeader } from '../ui/Card';
import Badge from '../ui/Badge';
import { formatMoney } from '../../utils/formatters';
import { getInventoryExcluded } from '../../api/mec';

// Per D-005 / D-014: surface what was excluded from Matched COGS so the
// bookkeeper can audit against Tekmetric inventory usage. Loads on
// expand, not on mount — avoids hammering Redshift before the user cares.

function addDaysISO(iso, n) {
  const d = new Date(iso + 'T00:00:00');
  d.setDate(d.getDate() + n);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export default function InventoryExclusionCard({ selectedShops, periodStart, periodEndInclusive }) {
  const [expanded, setExpanded] = useState(false);
  const [byShop, setByShop] = useState({}); // {shop_id: {grand_total_cents, breakdown}}
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notDeployed, setNotDeployed] = useState(false);

  // When expanded for the first time (or after deps change), fetch for
  // every selected shop in parallel. Treats a 404 as "endpoint not deployed
  // yet" and gracefully degrades.
  useEffect(() => {
    if (!expanded) return;
    if (!selectedShops?.length || !periodStart || !periodEndInclusive) return;
    let alive = true;
    setLoading(true);
    setError(null);
    setNotDeployed(false);
    const periodEndExclusive = addDaysISO(periodEndInclusive, 1);
    Promise.allSettled(
      selectedShops.map((s) =>
        getInventoryExcluded(s.shop_id, periodStart, periodEndExclusive)
          .then((data) => ({ shop_id: s.shop_id, data }))
      )
    ).then((results) => {
      if (!alive) return;
      const out = {};
      let any404 = false;
      let anySuccess = false;
      for (const r of results) {
        if (r.status === 'fulfilled') {
          anySuccess = true;
          out[r.value.shop_id] = r.value.data;
        } else if (/404|not found/i.test(r.reason?.message || '')) {
          any404 = true;
        }
      }
      setByShop(out);
      if (!anySuccess && any404) setNotDeployed(true);
      setLoading(false);
    });
    return () => { alive = false; };
  }, [expanded, JSON.stringify(selectedShops?.map((s) => s.shop_id)), periodStart, periodEndInclusive]);

  const grandTotal = Object.values(byShop).reduce(
    (s, x) => s + (x?.grand_total_cents || 0),
    0
  );
  const distinctParts = Object.values(byShop).reduce(
    (s, x) => s + (x?.breakdown?.length || 0),
    0
  );

  return (
    <Card padded={false}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="w-full text-left p-5 hover:bg-[var(--color-cream-100)]/50 transition-colors rounded-[var(--radius-lg)]"
      >
        <div className="flex items-start gap-4">
          <div className="w-9 h-9 rounded-[var(--radius-md)] bg-[var(--color-cream-100)] flex items-center justify-center text-[var(--color-warm-500)] shrink-0 [&_svg]:w-[18px] [&_svg]:h-[18px]">
            <Boxes />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] font-bold uppercase tracking-[0.8px] text-[var(--color-warm-500)]">
              Inventory-Sourced Parts Excluded
            </div>
            <div className="flex items-baseline gap-3 mt-1">
              <span className="text-[22px] font-extrabold text-[var(--color-warm-900)] tracking-tight tabular-nums">
                {expanded && loading ? '…' : formatMoney(grandTotal / 100)}
              </span>
              {distinctParts > 0 && (
                <Badge tone="cream" size="xs">
                  {distinctParts} distinct parts
                </Badge>
              )}
            </div>
            <div className="text-[12px] text-[var(--color-warm-600)] mt-1 leading-snug">
              D-005 — bulk oil, filter stock, and other inventory-dispensed parts removed from Matched COGS so they don't double-expense.
            </div>
          </div>
          <div className="shrink-0 mt-1 text-[var(--color-warm-400)]">
            {expanded ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          </div>
        </div>
      </button>

      {expanded && (
        <div className="border-t border-[var(--color-warm-100)] px-5 py-4">
          {notDeployed && (
            <div className="flex items-start gap-2 p-3 rounded-[var(--radius-md)] bg-[var(--color-action-50)] border border-[var(--color-action-100)] text-[12px] text-[var(--color-action-700)]">
              <Info size={14} className="shrink-0 mt-0.5" />
              <div>
                The inventory-exclusion breakdown endpoint isn't deployed yet. Total above is from cached close runs. The Backend agent is wiring this in parallel.
              </div>
            </div>
          )}
          {error && (
            <div className="flex items-start gap-2 p-3 rounded-[var(--radius-md)] bg-[var(--color-danger-50)] border border-[var(--color-danger-100)] text-[12px] text-[var(--color-danger-700)]">
              <AlertCircle size={14} className="shrink-0 mt-0.5" />
              <div>{error}</div>
            </div>
          )}
          {!notDeployed && !error && loading && (
            <div className="text-[12px] text-[var(--color-warm-500)] py-4">Loading per-part breakdown…</div>
          )}
          {!notDeployed && !error && !loading && distinctParts === 0 && (
            <div className="text-[12px] text-[var(--color-warm-500)] py-4">
              No inventory-sourced parts excluded in this period.
            </div>
          )}
          {!notDeployed && !error && !loading && distinctParts > 0 && (
            <ExclusionTable byShop={byShop} selectedShops={selectedShops} />
          )}
        </div>
      )}
    </Card>
  );
}

function ExclusionTable({ byShop, selectedShops }) {
  // Flatten: one row per (shop, brand, name). Sort by total_cents DESC.
  const rows = [];
  for (const s of selectedShops) {
    const data = byShop[s.shop_id];
    if (!data?.breakdown) continue;
    for (const b of data.breakdown) {
      rows.push({
        shop_name: s.shop_name || `Shop ${s.shop_id}`,
        brand: b.brand || '—',
        name: b.name || '—',
        line_count: b.line_count || 0,
        total_cents: b.total_cents || 0,
      });
    }
  }
  rows.sort((a, b) => b.total_cents - a.total_cents);
  const top = rows.slice(0, 20);
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-[12px]">
        <thead>
          <tr className="text-left text-[10px] font-bold uppercase tracking-[1px] text-[var(--color-warm-500)]">
            <th className="px-3 py-2">Shop</th>
            <th className="px-3 py-2">Brand</th>
            <th className="px-3 py-2">Part</th>
            <th className="px-3 py-2 text-right">Lines</th>
            <th className="px-3 py-2 text-right">Total</th>
          </tr>
        </thead>
        <tbody>
          {top.map((r, i) => (
            <tr key={i} className="border-t border-[var(--color-warm-100)] hover:bg-[var(--color-cream-100)]/50">
              <td className="px-3 py-2 text-[var(--color-warm-700)]">{r.shop_name}</td>
              <td className="px-3 py-2 text-[var(--color-warm-800)] font-medium">{r.brand}</td>
              <td className="px-3 py-2 text-[var(--color-warm-600)]">{r.name}</td>
              <td className="px-3 py-2 text-right tabular-nums text-[var(--color-warm-600)]">{r.line_count}</td>
              <td className="px-3 py-2 text-right tabular-nums font-semibold text-[var(--color-warm-900)]">
                {formatMoney(r.total_cents / 100)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length > top.length && (
        <div className="text-[11px] text-[var(--color-warm-500)] mt-2 px-3">
          Showing top 20 of {rows.length} rows. Full breakdown will be exportable in v1.1.
        </div>
      )}
    </div>
  );
}
